import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import DelayedPasswordInput from "@/components/DelayedPasswordInput";
import EUROPEAN_COUNTRIES from "@/lib/europeanCountries";
import ToniniVisionCTA from "@/components/ToniniVisionCTA";
import NasaHubCTA from "@/components/NasaHubCTA";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    country: "",
    referral_code: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = params.get("ref");
    // NOTE: do NOT uppercase – admin referral codes can be case-sensitive
    // (e.g. "NASHAcom") and the backend matches case-insensitively anyway.
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password !== form.confirmPassword) {
      setErr("Lozinke se ne poklapaju!");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center px-4 py-10 overflow-hidden">
      <div className="absolute inset-0 circuit-bg opacity-25" />
      <div className="absolute inset-0 bg-black/85" />
      <div className="absolute inset-0 nasa-grid-bg opacity-50" />

      <div className="relative w-full max-w-xl">
        <a href="https://www.myner-adria.com" className="absolute -top-8 left-0 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-[#22ff77] transition-colors">
          ← Početna
        </a>
        <div className="flex flex-col items-center mb-8">
          <img
            src="/logo.png"
            alt="NAŠA Myner Adria"
            style={{mixBlendMode:"screen"}} className="w-36 sm:w-44"
          />
          <h1 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-[0.05em]">
            <span className="text-neon-green">NAŠA</span>{" "}
            <span className="text-neon-orange">MYNER</span>
          </h1>
          <div className="mt-3 flex items-center gap-2">
            <span className="h-px w-6 bg-[#ff7a18]/60"></span>
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#ff7a18] font-display font-semibold">
              Adria Region — Giveaway strategija
            </p>
            <span className="h-px w-6 bg-[#ff7a18]/60"></span>
          </div>
        </div>

        <form
          data-testid="register-form"
          onSubmit={onSubmit}
          className="nasa-card nasa-card-bg-man p-8 space-y-5 backdrop-blur-sm"
        >
          <div>
            <h2 className="font-display text-xl font-semibold tracking-wide">
              Registracija
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              Aktivirajte nalog prvom kupovinom — <span className="text-[#22ff77]">25€ = 25 NAŠA Tokena</span>.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Ime i prezime" required>
              <input data-testid="reg-name" required value={form.name} onChange={set("name")}
                className="nasa-input" placeholder="Vaše ime" />
            </Field>
            <Field label="Email">
              <input data-testid="reg-email" required type="email" value={form.email} onChange={set("email")}
                className="nasa-input" placeholder="vasa@adresa.com" />
            </Field>
            <Field label="Lozinka" required>
              <DelayedPasswordInput data-testid="reg-password" required value={form.password} onChange={set("password")}
                autoComplete="new-password"
                className="nasa-input" placeholder="min. 6 karaktera" />
            </Field>
            <Field label="Potvrdi lozinku" required>
              <DelayedPasswordInput data-testid="reg-confirm-password" required value={form.confirmPassword} onChange={set("confirmPassword")}
                autoComplete="new-password"
                className="nasa-input" placeholder="ponovi lozinku" />
            </Field>
            <Field label="Telefon">
              <input data-testid="reg-phone" value={form.phone} onChange={set("phone")}
                className="nasa-input" placeholder="+381 ..." />
            </Field>
            <Field label="Država" required>
              <select data-testid="reg-country" required value={form.country} onChange={set("country")}
                className="nasa-input">
                <option value="">— izaberi državu —</option>
                {EUROPEAN_COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Referral Code" required>
              <input
                data-testid="reg-referral"
                required
                value={form.referral_code}
                onChange={set("referral_code")}
                className="nasa-input uppercase"
                placeholder="OBAVEZNO · npr. ABCD1234"
                aria-required="true"
              />
            </Field>
          </div>

          {err && (
            <div data-testid="register-error" className="border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-3 py-2 rounded-sm">
              {err}
            </div>
          )}

          <button
            data-testid="register-submit"
            disabled={loading}
            type="submit"
            className="w-full flex items-center justify-center gap-2 bg-[#22ff77] text-black font-bold py-3 uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 transition-all glow-green disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Registruj se
          </button>

          <div className="text-center text-xs text-zinc-500 pt-2 border-t border-white/5">
            Već imate nalog?{" "}
            <Link to="/prijava" data-testid="go-login" className="text-[#ff7a18] hover:underline font-semibold">
              Prijavi se
            </Link>
          </div>
        </form>
      </div>

      <div className="hidden sm:block">
        <ToniniVisionCTA />
        <NasaHubCTA />
      </div>
      <div className="block sm:hidden w-full mt-6">
        <ToniniVisionCTA />
        <NasaHubCTA />
      </div>

      <style>{`
        .nasa-input {
          width: 100%;
          background: #000;
          border: 1px solid rgba(255,255,255,0.1);
          padding: 0.75rem;
          font-size: 0.875rem;
          border-radius: 2px;
          color: #fff;
          transition: border-color 0.2s;
        }
        .nasa-input:focus { outline: none; border-color: #22ff77; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1">
      <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500">
        {label}
        {required && <span className="text-[#ff7a18] ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
